import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { getDashboardSalesChart } from "../../../services";
import { DashboardSalesChartResponse } from "../../../types/dashboard.type";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import SalesBarChart from "../SalesBarChart";
import ChartWithSummarySkeleton from "../Skeleton/ChartWithSummarySkeleton";

/**
 * "ยอดรายสาขา" — per-branch sales vs target.
 *
 * Standalone chart, distinct from the period sales-summary graph (section 5).
 * It is NOT bound to the รายวัน/รายเดือน/รายปี selector: it always shows the
 * per-branch breakdown (the backend's `period=today` branch-grouped mode) and
 * is only rendered by the parent when the branch filter is "ทุกสาขา".
 */
export default function BranchSalesChart() {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [chart, setChart] = useState<DashboardSalesChartResponse>();

  const onGetBranchSales = useCallback(async () => {
    setLoading(true);
    setError(false);
    const today = dayjs().format("YYYY-MM-DD");
    try {
      const {
        data: { data },
      } = await getDashboardSalesChart("today", "sales", today, today, "all");
      setChart(data);
    } catch {
      setError(true);
      setChart(undefined);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    onGetBranchSales();
  }, [onGetBranchSales]);

  const chartData = useMemo(() => {
    if (!chart?.breakdown) return [];
    return chart.breakdown.map((item, index) => ({
      date: item.label,
      amount: item.value,
      dateIndex: index % 7,
      target: item.target,
    }));
  }, [chart?.breakdown]);

  // Single representative target reference line (max per-branch target).
  const saleTarget = useMemo(() => {
    if (!chart?.breakdown?.length) return undefined;
    return chart.breakdown.reduce(
      (max, item) => Math.max(max, item.target ?? 0),
      0,
    );
  }, [chart?.breakdown]);

  return (
    <Paper>
      <Typography variant="body1" fontWeight={600} mb={2}>
        ยอดรายสาขา
      </Typography>

      {loading && <ChartWithSummarySkeleton />}

      {!loading && error && (
        <Box
          sx={{
            height: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="error" fontWeight={500}>
            ไม่สามารถโหลดข้อมูลได้
          </Typography>
        </Box>
      )}

      {!loading && !error && chartData.length === 0 && (
        <Box
          sx={{
            height: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="text.secondary" fontWeight={500}>
            ไม่พบข้อมูล
          </Typography>
        </Box>
      )}

      {!loading && !error && chartData.length > 0 && (
        <SalesBarChart
          data={chartData}
          saleTarget={saleTarget}
          colorMode="performance"
          showValueLabels={false}
          valueUnit="บาท"
        />
      )}
    </Paper>
  );
}
